//
// # Connections
//

import { Result } from "../errors";
import { ScalarName, BusName, ScalarOrBusName } from "./name";
import { type PortDir } from "./portdir";
import { SchPort, ValidPortName } from "./schport";
import { ConnKind, SignalUsage } from "./conns_data";

// // # Signal
// export interface Signal {
//   connKind: ConnKind.Signal; // Connection Kind Discriminator
//   dir: PortDir | null; // Port Kind/ Direction. Reserved for `SchPort` variants.
//   usage: SignalUsage; // Signal Usage
//   name: string;
//   width: number; // Width in bits
// }
// export const Signal = {
//   // Create a new `Signal`.
//   new: (
//     signalName: SignalName,
//     usage: SignalUsage = SignalUsage.Signal
//   ): Signal => ({
//     connKind: ConnKind.Signal,
//     dir: null,
//     name: signalName.raw,
//     width: 1, // FIXME!
//     usage,
//   }),
//   parse: (src: string): Result<Signal> => {
//     const signalNameResult = SignalName.parse(src);
//     if (signalNameResult.err) return signalNameResult;
//     const signalName = signalNameResult.val;
//     return Result.Ok(Signal.new(signalName));
//   },
//   // Signal repr
//   // The string value generally printed in the schematic.
//   // For a multi-bit signal named `foo`, this generally looks like `foo<3:0>`,
//   // i.e. uses the slice notation, with a right index of zero.
//   repr: (s: Signal): string => {
//     if (s.width > 1) {
//       return `${s.name}<${s.width - 1}:0>`;
//     }
//     return s.name;
//   },
// };

// // Validated signal name
// interface SignalName {
//   connKind: ConnKind.Signal;
//   raw: string;
// }
// export const SignalName = {
//   parse: (src: string): Result<SignalName> => {
//     //
//     // Format:
//     // - Start with `[a-zA-Z]`, i.e. an alpha character
//     // - Follow with zero or more alphanumerics (`[a-zA-Z0-9]`), underscores, and/or some_weirder stuff.
//     //
//     // Similar to (fairly simple) identifier rules in many programming languages.
//     // But with exclamation points! And plus and minus! And... ok maybe not normal program identifiers.
//     //
//     // NOTE there is almost certainly a JavaScript way to make this one regex comparison, we just don't know it.
//     //
//     if (!src.length) return Result.Err("Empty signal name");
//     const [first, ...rest] = src;
//     if (!first.match(/[a-zA-Z]/))
//       return Result.Err({ msg: "Invalid signal name", src });
//     for (const char of rest) {
//       if (!char.match(/[a-zA-Z0-9_!+-]/))
//         return Result.Err({ msg: "Invalid signal name", src });
//     }
//     return Result.Ok({ connKind: ConnKind.Signal, raw: src });
//   },

//   // String representation
//   repr: (s: SignalName): string => {
//     return s.raw;
//   },
// };
// // # Signal Slice
// // Integer index into a `Signal`, or a range of indices.
// export interface Slice {
//   connKind: ConnKind.Slice;
//   signalName: SignalName;
//   left: number; // Left or primary index
//   right: number | undefined; // Right index. Undefined for a single integer index.
// }

// export const Slice = {
//   new: (signalName: SignalName, left: number, right?: number): Slice => {
//     return { connKind: ConnKind.Slice, signalName, left, right };
//   },
//   // Format: `sigName<1>` or `sigName<2:0>`
//   parse: (src: string): Result<Slice> => {
//     const parts = src.split("<");
//     if (parts.length !== 2) {
//       const msg = `Invalid slice ${src}`;
//       return Result.Err(msg);
//     }
//     const [leftSide, rightSide] = parts;

//     const signalNameResult = SignalName.parse(leftSide);
//     if (signalNameResult.err) return signalNameResult;
//     const signalName = signalNameResult.val;

//     if (!rightSide.endsWith(">")) {
//       const msg = `Invalid slice ${src}`;
//       return Result.Err(msg);
//     }
//     const indexSrc = rightSide.slice(0, -1);
//     const indexParts = indexSrc.split(":");
//     if (indexParts.length < 1 || indexParts.length > 2) {
//       const msg = `Invalid slice ${src}`;
//       return Result.Err(msg);
//     }
//     // Length is either 1 (scalar) or 2 (range).
//     //  the left index either way.
//     const left = parseInt(indexParts[0]);
//     if (isNaN(left)) {
//       const msg = `Invalid slice ${src}`;
//       return Result.Err(msg);
//     }
//     if (indexParts.length === 1) {
//       // Scalar.
//       return Result.Ok(Slice.new(signalName, left));
//     }
//     // Range.  the right side.
//     const right = parseInt(indexParts[1]);
//     if (isNaN(right)) {
//       const msg = `Invalid slice ${src}`;
//       return Result.Err(msg);
//     }
//     return Result.Ok(Slice.new(signalName, left, right));
//   },

//   // String representation
//   repr: (s: Slice): string => {
//     // Format:
//     //  `sigName<1>` or `sigName<2:0>`
//     const sname = SignalName.repr(s.signalName);
//     if (s.right === undefined) {
//       return `${sname}<${s.left}>`;
//     }
//     return `${sname}<${s.left}:${s.right}>`;
//   },
// };

export class ConcatName {
  readonly nameKind = "concat";
  private constructor(
    readonly parts: Array<ScalarName | BusName | RepeatName>
  ) {}

  // Format: `sigName<1>` or `sigName<2:0>`
  static parse(src: string): Result<ConcatName> {
    // Format: `sig1,sig2,sig3<1>,sig4<2:0>,<*3>sig5` etc.
    //  the comma-separated list of connectables.
    const parts = [];
    for (const s of src.split(",")) {
      const r = Connectable.parse(s);
      if (r.err) return r;
      const connectable = r.val;

      if (connectable.nameKind === "concat") {
        // Shouldn't happen, but at minimum the type system demands we handle this.
        const msg = `Internal error: parsed ${src} as an invalid nested concat`;
        return Result.Err(msg);
      }
      parts.push(connectable);
    }
    return Result.Ok(new ConcatName(parts));
  }

  // String representation
  repr(): string {
    // Format: `sig1,sig2,sig3<1>,sig4<2:0>,<*3>sig5` etc.
    return this.parts.map((p) => p.repr()).join(",");
  }

  // Width in bits
  width(): number {
    // Sum of the widths of the parts
    return this.parts.map((p) => p.width()).reduce((sum, num) => sum + num, 0);
  }
}

export class RepeatName {
  readonly nameKind = "repeat";
  private constructor(
    readonly num: number,
    readonly target: ScalarName | BusName
  ) {}

  static new(num: number, target: ScalarName | BusName): RepeatName {
    return new RepeatName(num, target);
  }

  static parse(src: string): Result<RepeatName> {
    // Format: `<*N>signalName`
    if (!src.startsWith("<*")) {
      const msg = `Invalid repeat ${src}`;
      return Result.Err(msg);
    }
    const rest = src.slice(2);
    const parts = rest.split(">");
    if (parts.length !== 2) {
      const msg = `Invalid repeat ${src}`;
      return Result.Err(msg);
    }
    const num = parseInt(parts[0]);
    if (isNaN(num)) {
      const msg = `Invalid repeat ${src}`;
      return Result.Err(msg);
    }
    const signalNameResult = ScalarOrBusName.parse(parts[1]);
    if (signalNameResult.err) return signalNameResult;
    const signalName = signalNameResult.val;
    return Result.Ok(new RepeatName(num, signalName));
  }

  // String representation
  repr(): string {
    // Format: `<*N>targetName`
    return `<*${this.num}>${this.target.repr()}`;
  }

  // Width in bits
  width(): number {
    return this.num * this.target.width();
  }
}

// # Connectable
// The union of things that can annotate a wire connection, connect to an instance port, etc.
export type Connectable = ScalarName | BusName | ConcatName | RepeatName;
export const Connectable = {
  parse: (src: string): Result<Connectable> => {
    if (src.includes(",")) {
      return ConcatName.parse(src);
    }
    if (src.startsWith("<*")) {
      return RepeatName.parse(src);
    }
    return ScalarOrBusName.parse(src);
  },
};

export class Conns {
  ports: Map<string, SchPort> = new Map();
  connectables: Map<string, Connectable> = new Map();

  //
  addPort = (conn: ValidPortName, dir: PortDir): SchPort => {
    const key = conn.repr();
    const existing = this.ports.get(key);
    if (existing) return existing;
    const port = SchPort.new(conn, dir);
    this.ports.set(key, port);
    return port;
  };

  addConn = (conn: Connectable): Connectable => {
    const key = conn.repr();
    const existing = this.connectables.get(key);
    if (existing) return existing;
    this.connectables.set(key, conn);
    return conn;
  };
}
